import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAppAuth, useAppAuthActions, useAppTenantsState } from '../hooks/useAppAuth';
import { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import { SidebarProvider, SidebarInset } from '../components/ui/sidebar';
import AuthService from '../services/auth.service';
import FeatureFlagService from '../services/featureFlag.service';
import { FeatureFlag, useFeatureFlag } from '../services/featureFlag.service';
import { useWhitelabelOverrides } from '../hooks/useWhitelabelOverrides';
import { UnsavedChangesProvider } from '../contexts/UnsavedChangesContext';
import { SupportRequestStreamProvider } from '../contexts/SupportRequestStreamContext';
import { useUserRoles } from '../hooks/useUserRoles';
import { useTenantConfig } from '../hooks/useTenantConfig';
import { useUserConfig } from '../hooks/useUserConfig';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../services/api.service';

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

export default function AuthenticatedRootPage({ chromeless = false }: { chromeless?: boolean } = {}) {
  const { isAuthenticated: fronteggAuthenticated, isLoading, user } = useAppAuth();
  const { tenants } = useAppTenantsState();
  const location = useLocation();
  useWhitelabelOverrides();
  // Same gate as the Requests nav item — no point holding a stream open for a
  // user who can't see a request in the first place.
  const { canViewDataManagement } = useUserRoles();
  const { data: tenantConfig, error: configError, isError: isConfigError } = useTenantConfig();
  useUserConfig();

  // Check for system session auth (used by screengrabber/notifications)
  const hasSystemSession = !fronteggAuthenticated && !!AuthService.getInstance().getAuthState();
  const isAuthenticated = fronteggAuthenticated || hasSystemSession;

  // Sync Frontegg auth state with AuthService
  useEffect(() => {
    if (user && isAuthenticated) {
      // Create auth state from Frontegg
      // Map IRole[] to string[] for roles
      const roleNames = (user.roles || []).map(role =>
        typeof role === 'string' ? role : role.name || role.key || ''
      );
      const authState = {
        sub: user.id || '',
        name: user.name || '',
        email: user.email || '',
        tenant_id: user.tenantId || '',
        roles: roleNames,
        tenant_name: user.tenantId || 'Tenant',
        access_token: user.accessToken || '',
        profile_img_url: user.profilePictureUrl || undefined,
        tenants: [],
        customClaims: (user as any).customClaims || {},
      };
      AuthService.getInstance().setAuthState(authState);
      const currentTenant = tenants?.find((t: any) => t.tenantId === user.tenantId);
      // Prefer tenant name from our config API (always available) over
      // Frontegg's tenant list (empty on custom domains in Safari).
      const tenantName = (tenantConfig as any)?.tenant_name
        || currentTenant?.name
        || 'Tenant';
      const lowerRoles = roleNames.map(r => r.toLowerCase());
      const highestRole = lowerRoles.includes('admin') ? 'Admin'
        : lowerRoles.includes('builder') ? 'Builder'
        : lowerRoles.includes('readonly') ? 'ReadOnly'
        : lowerRoles.includes('readonly_partner') ? 'ReadOnly_Partner'
        : 'Unknown';
      FeatureFlagService.getInstance().identify({
        key: user.id || '',
        name: user.name || '',
        email: user.email || '',
        tenant: tenantName,
        role: highestRole,
      });
    }
  }, [user, isAuthenticated, tenantConfig]);

  // Handle tenant switch from custom domain — switchTo param in URL.
  // Initialize from URL so it's true on the first render (before effects run),
  // preventing the custom domain redirect from firing before the switch completes.
  const { switchTenant } = useAppAuthActions();
  const queryClient = useQueryClient();
  const [switchingTenant, setSwitchingTenant] = useState(
    () => !!new URLSearchParams(window.location.search).get('switchTo')
  );
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const switchTo = params.get('switchTo');
    if (switchTo && switchTo !== user?.tenantId) {
      setSwitchingTenant(true);
      // Clean URL
      params.delete('switchTo');
      const clean = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (clean ? '?' + clean : ''));
      switchTenant({ tenantId: switchTo });
    } else if (switchingTenant) {
      // Switch completed — tenant matches. Bust the cache so tenantConfig
      // refetches for the new tenant before the redirect effect runs.
      queryClient.removeQueries();
      setSwitchingTenant(false);
    }
  }, [isAuthenticated, user?.tenantId]);

  // Handle `inbound_tenant_id` — deep links (e.g. a Linear task's "Thread" link) carry the
  // tenant the content belongs to so we land in the right account. Same mechanic as
  // `switchTo`, but the param stays until the tenant flips, and if the user has no access
  // to that tenant we surface a dedicated screen instead of silently failing.
  const navigate = useNavigate();
  const [inboundNoAccess, setInboundNoAccess] = useState<string | null>(null);
  const [switchingInbound, setSwitchingInbound] = useState(
    () => !!new URLSearchParams(window.location.search).get('inbound_tenant_id')
  );
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const inbound = params.get('inbound_tenant_id');
    if (!inbound) {
      if (switchingInbound) setSwitchingInbound(false);
      return;
    }
    if (inbound === user?.tenantId) {
      // Already on (or finished switching to) the target — clean the URL and render.
      params.delete('inbound_tenant_id');
      const clean = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (clean ? '?' + clean : ''));
      if (switchingInbound) {
        queryClient.removeQueries();
        setSwitchingInbound(false);
      }
      return;
    }
    // The tenant list (populated on the main domain) is our access check. If it's loaded
    // and doesn't include the target, the user can't switch — show the no-access screen.
    if (tenants && tenants.length > 0 && !tenants.some((t: any) => t.tenantId === inbound)) {
      setSwitchingInbound(false);
      setInboundNoAccess(inbound);
      return;
    }
    setSwitchingInbound(true);
    const authService = AuthService.getInstance();
    if (authService.isCustomDomain) {
      // On a custom domain, hand off to the main domain to perform the switch (see
      // TenantSwitcher) — switchTenant() here would race the redirect.
      window.location.href = `${authService.authAppUrl}/?switchTo=${encodeURIComponent(inbound)}`;
      return;
    }
    switchTenant({ tenantId: inbound });
  }, [isAuthenticated, user?.tenantId, tenants]);


  // Redirect authenticated users to legacy frontend when force-legacy flag is enabled
  const forceLegacy = useFeatureFlag(FeatureFlag.FORCE_LEGACY);
  useEffect(() => {
    const authService = AuthService.getInstance();
    if (forceLegacy && authService.legacyAppUrl && isAuthenticated) {
      window.location.href = authService.legacyAppUrl;
    }
  }, [forceLegacy, isAuthenticated]);

  // Store return path for redirect after login
  useEffect(() => {
    if (location && !location.pathname.includes('/login') && !location.pathname.includes('/logout')) {
      const existingReturnPath = localStorage.getItem('returnPath');
      if (!existingReturnPath && location.hash !== '#returning') {
        localStorage.setItem('returnPath', location.pathname + location.search);
      }
    }
  }, [location]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const authService = AuthService.getInstance();
    if (authService.useExternalLogin) {
      // Locally, Frontegg OAuth is configured for a different port, so redirect there for login
      window.location.href = `${authService.authAppUrl}/login`;
      return null;
    }
    return <Navigate to="/login" replace />;
  }

  // No access to the inbound tenant — surface it with a way out.
  if (inboundNoAccess) {
    const returnToCurrent = () => {
      const params = new URLSearchParams(window.location.search);
      params.delete('inbound_tenant_id');
      const clean = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (clean ? '?' + clean : ''));
      setInboundNoAccess(null);
      navigate('/');
    };
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="max-w-md w-full text-center space-y-4 px-6">
          <h1 className="text-lg font-semibold text-foreground">Account not available</h1>
          <p className="text-sm text-muted-foreground">
            This link points to an account you don't have access to, so we couldn't switch you to it.
          </p>
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button variant="outline" onClick={() => window.close()}>
              Close window
            </Button>
            <Button onClick={returnToCurrent}>Go to my account</Button>
          </div>
        </div>
      </div>
    );
  }

  // Show a switching screen while moving to another tenant (custom-domain switchTo or an
  // inbound_tenant_id deep link).
  if (switchingTenant || switchingInbound) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-muted-foreground">Switching account…</div>
      </div>
    );
  }

  if (isConfigError && configError instanceof ApiError && configError.code === 404) {
    const payload = configError.payload as any;
    if (payload?.archived) {
      return <Navigate to="/tenant-archived" state={{ tenantName: payload.tenant_name }} replace />;
    }
    return <Navigate to="/tenant-not-found" replace />;
  }

  if (AuthService.getInstance().noDataAccess) {
    return <Navigate to="/no-data-access" replace />;
  }

  // Consent flows (the `plb` CLI handoff, the MCP connector) render without the
  // app shell: they are a single focused decision reached from outside the app,
  // often in a popup, and a sidebar full of navigation invites the user to
  // wander off mid-authorization. Every gate above this point still applies —
  // login, tenant access, archived/not-found — so this is purely the chrome.
  if (chromeless) {
    return (
      <UnsavedChangesProvider>
        <Outlet />
      </UnsavedChangesProvider>
    );
  }

  return (
    // Wraps the sidebar as well as the page: the unread badge lives up there and
    // is one of the things the stream keeps current.
    <SupportRequestStreamProvider enabled={canViewDataManagement}>
      <SidebarProvider>
        <Sidebar />
        <SidebarInset>
          <UnsavedChangesProvider>
            <Outlet />
          </UnsavedChangesProvider>
        </SidebarInset>
      </SidebarProvider>
    </SupportRequestStreamProvider>
  );
}

